import { ANNOUNCEMENT_ICON_XML as ICON } from '../../../shared/announcementIcons.js'
import { SECTION_HERO_XML } from '../../../shared/onboardingSectionArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import Prose from '../components/ds/Prose'
import { landingJsonLd } from '../lib/landingJsonLd'

// ── /about ───────────────────────────────────────────────────────────────────
// The page that answers "who are these people and is this real".
//
// Every marketplace gets searched for this and almost none answers it. It
// matters more for us than for most: we ask a professional to hand over a
// passport before they have earned a penny, and we ask a workplace to let a
// stranger into their building. Both of those are a leap of faith in a name
// nobody has heard of yet.
//
// So this page is deliberately concrete rather than warm: what the company is,
// who is accountable for it, where the money sits, and what we will not do. The
// statutory block in the footer says the same thing in legal language; this says
// it in English.
//
// ⛔ No founder mythology, no "we believe", no stock photo of a team that does
// not exist. If we cannot evidence it, it is not on the page.

export default function About() {
  return (
    <div className="bg-canvas">
      <Seo
        title="About TempDash"
        description="Who runs TempDash, how the company is registered, where your money actually sits, and the things we have decided not to do. The plain English version."
        path="/about"
        jsonLd={landingJsonLd}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        art={SECTION_HERO_XML.locations}
        eyebrow="About"
        title="A small company doing one thing properly."
        lead="We are building the software that replaces an agency, for the people on both sides of a shift. That’s the whole of it."
      />

      <Section ground="canvas">
        <div className="md:flex md:items-start md:gap-16">
          <Art xml={SECTION_HERO_XML.business} className="h-[150px] w-[200px] shrink-0" />
          <div className="mt-8 max-w-[640px] md:mt-0">
            <SectionHead eyebrow="What we actually are" title={<>Software, not a staffing business.</>} />
            <p className="mt-5 text-[19px] leading-relaxed text-ink-muted">
              TempDash is a technology platform. A workplace posts a shift, a self employed
              professional accepts it, and they agree the work directly with each other. We run the
              checks, we handle the money, and we stay out of the rest.
            </p>
            <p className="mt-4 text-[19px] leading-relaxed text-ink-muted">
              We do not employ anyone who works a shift. We do not supply them, supervise them, or
              decide what they are worth. That’s not a disclaimer, it’s the design: it’s why a
              professional sets their own availability and a workplace sets its own rate.
            </p>
          </div>
        </div>
      </Section>

      <Section ground="navy">
        <div className="max-w-[760px]">
          <SectionHead on="navy" eyebrow="Why we built it" title={<>Two people, one shift, and a fortnight of admin between them.</>} />
          <p className="mt-6 text-[19px] leading-relaxed text-white/75">
            A workplace rings round on the morning somebody calls in sick. A professional works the
            day and then waits weeks to be paid, chasing an invoice nobody wants to deal with. In
            between sits a markup that neither of them chose.
          </p>
          <p className="mt-4 text-[19px] leading-relaxed text-white/75">
            None of that is anybody’s fault. It’s just what happens when the only way to connect
            two people is a phone and a spreadsheet. It is a software problem, and software is
            cheaper than a middleman.
          </p>
        </div>
      </Section>

      <Section ground="surface" width="text">
        <div className="space-y-12">
          <Prose id="company" art={ICON.compliance} title="The company">
            <p>
              TempDash is a trading name of <strong className="font-semibold text-ink">TempDash Ltd</strong>,
              registered in England and Wales, company number 17264372, registered office 124 City
              Road, London EC1V 2NX. We are registered with the Information Commissioner’s Office
              under ZC159507, which you can check on the ICO’s public register.
            </p>
            <p>
              Those are in the footer of every page too, because the Companies Act requires it. They
              are here as well because a company that is hard to look up is a company worth
              wondering about.
            </p>
          </Prose>

          <Prose id="money" art={ICON.wallet} title="Where the money sits">
            <p>
              Not with us. Worker pay is held by a regulated payments provider and moves to the
              person who earned it. We are the conduit, not the bank, and that is deliberate: it
              means your pay is not exposed to how our month is going.
            </p>
            <p>
              The detail is on <a className="text-link underline underline-offset-4" href="/getting-paid">getting paid</a>.
            </p>
          </Prose>

          <Prose id="not-doing" art={ICON.safety} title="Things we have decided not to do">
            <p>
              <strong className="font-semibold text-ink">We do not sell your data.</strong> Not to
              recruiters, not to anybody. Your documents are checked and held securely, and a
              workplace sees that you are cleared, not the documents themselves.
            </p>
            <p>
              <strong className="font-semibold text-ink">We do not take a cut of a raised rate.</strong>{' '}
              If a workplace puts a shift’s pay up to fill it, every extra pound goes to the person
              working it.
            </p>
            <p>
              <strong className="font-semibold text-ink">We do not hide what a shift pays.</strong>{' '}
              The rate is on the shift before anyone accepts it.
            </p>
          </Prose>

          <Prose id="where" art={ICON.location} title="Where we are">
            <p>
              London first, and we are adding areas as fast as we can do it properly. Dentistry is
              where we started because it is where the problem is sharpest, and the platform is
              built to carry other sectors without being rebuilt.
            </p>
          </Prose>

          <Prose id="talk" art={ICON.support} title="Talking to us">
            <p>
              <a className="text-link underline underline-offset-4" href="mailto:support@tempdash.app">support@tempdash.app</a>{' '}
              reaches a person, not a queue. If something is wrong, that is the fastest way to tell
              us, and we would rather hear it than not.
            </p>
          </Prose>
        </div>
      </Section>

      <CtaBand title="Have a look for yourself.">
        <Button to="/">Find work</Button>
        <Button to="/business" variant="outline">Get cover</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
